import _ from 'lodash';

import { PRESENCE_NOTIFICATION_TYPE, PRESENCE_REJOIN_GRACE_MS } from 'constants/presence';
import { jsonParse } from 'utils/json';

import { RESET_STATE } from 'store/global/actions';
// Follow-mode auto-stop on tab change is a loan-editor concern (the only surface that drives
// navigation via SET_ACTIVE_TAB) layered on top of the otherwise entity-generic follow state.
import { CONTROL_UI_ACTION_TYPE } from 'store/loanApplications/actions/controlUI.actions';

import { PRESENCE_ACTION_TYPE } from '../actions/presence.actions';

const parseMetadata = metadata => {
  if (_.isNil(metadata)) {
    return null;
  }

  try {
    return _.isString(metadata) ? jsonParse(metadata) : metadata;
  } catch {
    return null;
  }
};

const decodeUserLocation = user => {
  const parsedMetadata = parseMetadata(user.metadata);
  const tab = parsedMetadata?.tab ?? user.tab;
  const rawSectionId = parsedMetadata?.sectionId ?? user.sectionId;
  const hasEncodedSubtab = rawSectionId?.includes('::');

  const subtab = hasEncodedSubtab
    ? rawSectionId.split('::', 2)[0]
    : (parsedMetadata?.subtab ?? user.subtab);

  const sectionId = hasEncodedSubtab ? rawSectionId.split('::', 2)[1] || null : rawSectionId;

  return {
    ...user,
    tab,
    sectionId,
    subtab,
    entityId: parsedMetadata?.entityId ?? user.entityId,
    subsection: parsedMetadata?.subsection ?? user.subsection,
    ...(!_.isNil(parsedMetadata) && { metadata: parsedMetadata }),
  };
};

const initialState = {
  users: {},
  fieldLocks: {},
  remoteFieldValues: {},
  remoteFieldValuesClearReason: null,
  localEditedFieldPaths: [],
  notifications: [],
  isConnected: false,
  currentConnectionId: null,
  followingUserId: null,
  followingConnectionId: null,
  isFollowNavigating: false,
  recentDepartures: {},
};

const pruneRecentDepartures = (recentDepartures, now) =>
  Object.fromEntries(
    Object.entries(recentDepartures).filter(
      ([, occurredAt]) => now - occurredAt <= PRESENCE_REJOIN_GRACE_MS,
    ),
  );

const presenceReducer = (state = initialState, { type, payload }) => {
  switch (type) {
    case PRESENCE_ACTION_TYPE.SET_PRESENCE_SNAPSHOT: {
      const users = _.isNil(payload?.users)
        ? initialState.users
        : payload.users.reduce((acc, user) => {
            // The snapshot carries no original join time, so "now" is a floor: it degrades
            // "how long they've had it open" to "since I noticed them" rather than blank, and
            // a later USER_JOINED overrides it.
            acc[user.connectionId] = {
              occurredAt: Date.now(),
              ...decodeUserLocation(user),
              entityRef: payload?.entityRef ?? null,
            };

            return acc;
          }, {});

      const fieldLocks = _.isNil(payload?.fieldLocks)
        ? initialState.fieldLocks
        : payload.fieldLocks.reduce((acc, lock) => {
            acc[lock.fieldPath] = lock;

            return acc;
          }, {});

      return {
        ...state,
        users,
        fieldLocks,
        isConnected: true,
        currentConnectionId: payload?.currentConnectionId ?? null,
      };
    }

    case PRESENCE_ACTION_TYPE.USER_JOINED: {
      const hasExistingConnection = Object.values(state.users).some(
        user => user.userId === payload.userId,
      );
      const departedAt = state.recentDepartures[payload.userId];
      const isRecentDeparture =
        !_.isNil(departedAt) && payload.occurredAt - departedAt <= PRESENCE_REJOIN_GRACE_MS;
      const shouldAppendNotification =
        !payload.isSelfConnection && !hasExistingConnection && !isRecentDeparture;

      const notificationsWithoutDeparture = isRecentDeparture
        ? state.notifications.filter(
            notification =>
              !(
                notification.type === PRESENCE_NOTIFICATION_TYPE.LEFT &&
                notification.userId === payload.userId
              ),
          )
        : state.notifications;

      return {
        ...state,
        users: {
          ...state.users,
          [payload.connectionId]: {
            ...decodeUserLocation(payload),
            entityRef: payload.entityRef ?? null,
          },
        },
        recentDepartures: pruneRecentDepartures(
          isRecentDeparture
            ? _.omit(state.recentDepartures, payload.userId)
            : state.recentDepartures,
          payload.occurredAt,
        ),
        notifications: shouldAppendNotification
          ? [
              ...notificationsWithoutDeparture,
              {
                id: payload.notificationId,
                type: PRESENCE_NOTIFICATION_TYPE.JOINED,
                userId: payload.userId,
                displayName: payload.displayName,
                firstName: payload.firstName,
                lastName: payload.lastName,
                avatarUrl: payload.avatarUrl,
              },
            ]
          : notificationsWithoutDeparture,
      };
    }

    case PRESENCE_ACTION_TYPE.USER_LEFT: {
      const remainingUsers = _.omit(state.users, payload.connectionId);
      const hasOtherConnectionsForUser = Object.values(remainingUsers).some(
        user => user.userId === payload.userId,
      );

      const leavingLockedFieldPaths = new Set(
        Object.entries(state.fieldLocks)
          .filter(([, lock]) => lock.connectionId === payload.connectionId)
          .map(([fieldPath]) => fieldPath),
      );

      return {
        ...state,
        users: remainingUsers,
        fieldLocks: Object.entries(state.fieldLocks).reduce((acc, [fieldPath, lock]) => {
          if (lock.connectionId !== payload.connectionId) {
            acc[fieldPath] = lock;
          }

          return acc;
        }, {}),
        remoteFieldValues: hasOtherConnectionsForUser
          ? Object.entries(state.remoteFieldValues).reduce((acc, [fieldPath, entry]) => {
              if (!leavingLockedFieldPaths.has(fieldPath)) {
                acc[fieldPath] = entry;
              }

              return acc;
            }, {})
          : Object.entries(state.remoteFieldValues).reduce((acc, [fieldPath, entry]) => {
              if (entry.userId !== payload.userId) {
                acc[fieldPath] = entry;
              }

              return acc;
            }, {}),
        recentDepartures: pruneRecentDepartures(
          { ...state.recentDepartures, [payload.userId]: payload.occurredAt },
          payload.occurredAt,
        ),
        notifications:
          payload.isSelfConnection || hasOtherConnectionsForUser
            ? state.notifications
            : [
                ...state.notifications,
                {
                  id: payload.notificationId,
                  type: PRESENCE_NOTIFICATION_TYPE.LEFT,
                  userId: payload.userId,
                  displayName: payload.displayName,
                  firstName: payload.firstName,
                  lastName: payload.lastName,
                  avatarUrl: payload.avatarUrl,
                },
              ],
      };
    }

    case PRESENCE_ACTION_TYPE.USER_LOCATION_CHANGED: {
      if (_.isNil(state.users[payload.connectionId])) {
        return state;
      }

      const decoded = decodeUserLocation(payload);

      return {
        ...state,
        users: {
          ...state.users,
          [payload.connectionId]: {
            ...state.users[payload.connectionId],
            tab: decoded.tab,
            sectionId: decoded.sectionId,
            subtab: decoded.subtab,
            entityId: decoded.entityId,
            subsection: decoded.subsection,
            scrollTop: decoded.scrollTop,
            metadata: decoded.metadata ?? null,
          },
        },
      };
    }

    case PRESENCE_ACTION_TYPE.USER_STATUS_CHANGED: {
      if (_.isNil(state.users[payload.connectionId])) {
        return state;
      }

      return {
        ...state,
        users: {
          ...state.users,
          [payload.connectionId]: {
            ...state.users[payload.connectionId],
            status: payload.status,
            lastActivityAt: payload.changedAt ?? state.users[payload.connectionId].lastActivityAt,
          },
        },
      };
    }

    case PRESENCE_ACTION_TYPE.FIELD_LOCK_ACQUIRED:
      return {
        ...state,
        fieldLocks: {
          ...state.fieldLocks,
          [payload.fieldPath]: payload,
        },
      };

    case PRESENCE_ACTION_TYPE.FIELD_LOCK_RELEASED:
      return {
        ...state,
        fieldLocks: _.omit(state.fieldLocks, payload.fieldPath),
      };

    case PRESENCE_ACTION_TYPE.FIELD_LOCK_DENIED:
      return state;

    case PRESENCE_ACTION_TYPE.FIELD_VALUE_CHANGING:
      return {
        ...state,
        remoteFieldValues: {
          ...state.remoteFieldValues,
          [payload.fieldPath]: {
            value: payload.currentValue,
            userId: payload.userId,
            status: 'changing',
          },
        },
      };

    case PRESENCE_ACTION_TYPE.FIELD_VALUE_COMMITTED:
      return {
        ...state,
        remoteFieldValues: {
          ...state.remoteFieldValues,
          [payload.fieldPath]: {
            value: payload.committedValue,
            userId: payload.userId,
            status: 'committed',
          },
        },
      };

    case PRESENCE_ACTION_TYPE.FIELD_VALUES_REVERTED:
      return {
        ...state,
        remoteFieldValues: _.isNil(payload?.fieldPaths)
          ? state.remoteFieldValues
          : _.omit(state.remoteFieldValues, payload.fieldPaths),
      };

    case PRESENCE_ACTION_TYPE.TRACK_LOCAL_FIELD_EDIT:
      return {
        ...state,
        localEditedFieldPaths: state.localEditedFieldPaths.includes(payload)
          ? state.localEditedFieldPaths
          : [...state.localEditedFieldPaths, payload],
      };

    case PRESENCE_ACTION_TYPE.CLEAR_LOCAL_EDITED_FIELDS:
      return {
        ...state,
        localEditedFieldPaths: initialState.localEditedFieldPaths,
      };

    case PRESENCE_ACTION_TYPE.SET_PENDING_FIELD_VALUES: {
      if (!payload?.length) {
        return state;
      }

      const pendingValues = payload.reduce((acc, entry) => {
        acc[entry.fieldPath] = {
          value: entry.currentValue,
          userId: entry.userId,
          status: 'changing',
        };

        return acc;
      }, {});

      return {
        ...state,
        remoteFieldValues: {
          ...state.remoteFieldValues,
          ...pendingValues,
        },
      };
    }

    case PRESENCE_ACTION_TYPE.SET_IS_CONNECTED:
      return {
        ...state,
        isConnected: payload,
      };

    case PRESENCE_ACTION_TYPE.DISMISS_NOTIFICATION:
      return {
        ...state,
        notifications: state.notifications.filter(({ id }) => id !== payload),
      };

    case PRESENCE_ACTION_TYPE.START_FOLLOWING:
      return {
        ...state,
        followingUserId: payload.userId,
        followingConnectionId: payload.connectionId,
        isFollowNavigating: true,
      };

    case PRESENCE_ACTION_TYPE.SET_FOLLOW_NAVIGATING:
      return {
        ...state,
        isFollowNavigating: payload,
      };

    case CONTROL_UI_ACTION_TYPE.SET_ACTIVE_TAB: {
      if (_.isNil(state.followingUserId)) {
        return state;
      }

      if (state.isFollowNavigating) {
        return {
          ...state,
          isFollowNavigating: false,
        };
      }

      return {
        ...state,
        followingUserId: initialState.followingUserId,
        followingConnectionId: initialState.followingConnectionId,
        isFollowNavigating: false,
      };
    }

    case PRESENCE_ACTION_TYPE.STOP_FOLLOWING:
      return {
        ...state,
        followingUserId: initialState.followingUserId,
        followingConnectionId: initialState.followingConnectionId,
        isFollowNavigating: initialState.isFollowNavigating,
      };

    case PRESENCE_ACTION_TYPE.CLEAR_REMOTE_FIELD_VALUES:
      return {
        ...state,
        remoteFieldValues: initialState.remoteFieldValues,
        remoteFieldValuesClearReason: payload?.reason ?? null,
      };

    case PRESENCE_ACTION_TYPE.CLEAR_PRESENCE_STATE:
      return {
        ...initialState,
        isConnected: state.isConnected,
      };

    case PRESENCE_ACTION_TYPE.RESET_PRESENCE:
    case RESET_STATE:
      return initialState;

    default:
      return state;
  }
};

export default presenceReducer;
